/**
 * Backformen: Größe aus dem Text lesen und Mengen über die Grundfläche
 * umrechnen. Eine Springform mit 28 cm statt 26 cm braucht etwa 16 % mehr
 * Teig, damit der Kuchen gleich hoch wird.
 */
import { formatDecimal } from './quantity.ts';

/** Größe einer Form in Zentimetern. */
export type PanSize =
  | { readonly shape: 'round'; readonly diameter: number }
  | { readonly shape: 'rect'; readonly width: number; readonly length: number };

/** Übliche Breite einer Kastenform, wenn nur die Länge angegeben ist. */
export const LOAF_PAN_WIDTH = 11;

/** Übliches Backblech, wenn keine Maße angegeben sind. */
export const BAKING_TRAY: PanSize = { shape: 'rect', width: 30, length: 40 };

/** Zahl mit Komma oder Punkt. */
function number(text: string | undefined): number {
  return Number((text ?? '').replace(',', '.'));
}

/**
 * Liest eine Form aus einem Text wie „Springform 26 cm“, „Ø 24 cm“,
 * „Blech 30 x 40 cm“ oder „Kastenform 30 cm“. Liefert null, wenn keine
 * Größe erkennbar ist.
 */
export function parsePan(text: string): PanSize | null {
  const lower = text.toLowerCase();
  const rect = /(\d+(?:[.,]\d+)?)\s*(?:x|×|\*)\s*(\d+(?:[.,]\d+)?)/.exec(lower);
  if (rect !== null) {
    const a = number(rect[1]);
    const b = number(rect[2]);
    if (a > 0 && b > 0) {
      return { shape: 'rect', width: Math.min(a, b), length: Math.max(a, b) };
    }
  }
  const size = /(\d+(?:[.,]\d+)?)\s*(?:cm)?/.exec(lower.replace(/ø|⌀/g, ' '));
  const value = size === null ? 0 : number(size[1]);
  if (/kasten/.test(lower) && value > 0) {
    return { shape: 'rect', width: LOAF_PAN_WIDTH, length: value };
  }
  if (/blech/.test(lower) && value === 0) {
    return BAKING_TRAY;
  }
  // Ohne Zentimeter, Ø oder Formwort ist eine Zahl eher eine Stückzahl („12 Muffins“).
  const looksLikePan = /cm|ø|⌀|form|durchmesser|torte/.test(lower);
  if (looksLikePan && value >= 8 && value <= 60) {
    return { shape: 'round', diameter: value };
  }
  return null;
}

/** Grundfläche in Quadratzentimetern. */
export function panArea(pan: PanSize): number {
  return pan.shape === 'round' ? Math.PI * (pan.diameter / 2) ** 2 : pan.width * pan.length;
}

/** Faktor für die Mengen, wenn statt der Form from die Form to benutzt wird. */
export function panFactor(from: PanSize, to: PanSize): number {
  const area = panArea(from);
  return area <= 0 ? 1 : panArea(to) / area;
}

/** Kurzbeschreibung einer Form, zum Beispiel „Ø 26 cm“ oder „20 × 30 cm“. */
export function panLabel(pan: PanSize): string {
  return pan.shape === 'round'
    ? `Ø ${formatDecimal(pan.diameter, 1)} cm`
    : `${formatDecimal(pan.width, 1)} × ${formatDecimal(pan.length, 1)} cm`;
}

/** Form als kurzer Text für die Adresszeile: „rund-26“ oder „eckig-20x30“. */
export function panToParam(pan: PanSize): string {
  return pan.shape === 'round' ? `rund-${pan.diameter}` : `eckig-${pan.width}x${pan.length}`;
}

/** Liest eine Form aus der Adresszeile. */
export function panFromParam(value: string | null): PanSize | null {
  if (value === null) {
    return null;
  }
  const round = /^rund-(\d+(?:\.\d+)?)$/.exec(value);
  if (round !== null) {
    const diameter = Number(round[1]);
    return diameter > 0 ? { shape: 'round', diameter } : null;
  }
  const rect = /^eckig-(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/.exec(value);
  if (rect !== null) {
    const width = Number(rect[1]);
    const length = Number(rect[2]);
    return width > 0 && length > 0 ? { shape: 'rect', width, length } : null;
  }
  return null;
}
